import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import config from "../config";

const { api } = config;

/** Chave sessionStorage do JWT tenant — não misturar com SA (DOC 09 § 4). */
export const TENANT_AUTH_STORAGE_KEY = "authUser";
/** Chave sessionStorage do JWT Super Admin (guard `super_admin`). */
export const SA_AUTH_STORAGE_KEY = "sa_authUser";
/** Header/ProfileDropdown escutam para refletir PATCH /me sem reload. */
export const TENANT_SESSION_UPDATED_EVENT = "brenvio:tenant-session-updated";

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.headers.post["Content-Type"] = "application/json";

/**
 * Raiz do host da API (sem `/api/v1`).
 * Rotas SA ficam em `/super-admin/*`, fora do prefixo versionado do tenant.
 */
export const getApiOrigin = (): string => {
  const base = (api.API_URL || "").replace(/\/$/, "");
  return base.replace(/\/api\/v1$/i, "") || base;
};

/** URL absoluta de um endpoint Super Admin (ex.: `/super-admin/login`). */
export const saUrl = (path: string): string => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${getApiOrigin()}${normalized}`;
};

const readStoredToken = (storageKey: string): string | null => {
  const raw = sessionStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.token || null;
  } catch {
    return null;
  }
};

const isSuperAdminRequestUrl = (url?: string): boolean => {
  if (!url) return false;
  return url.includes("/super-admin");
};

const isSuperAdminPage = (): boolean =>
  window.location.pathname.startsWith("/super-admin");

// Boot: Authorization inicial conforme a página atual (tenant vs SA).
const bootToken = isSuperAdminPage()
  ? readStoredToken(SA_AUTH_STORAGE_KEY)
  : readStoredToken(TENANT_AUTH_STORAGE_KEY);
if (bootToken) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + bootToken;
}

export type ApiSuccess<T = unknown> = {
  success: true;
  message?: string;
  data: T;
};

export type ApiFailure = {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
};

axios.interceptors.request.use((request) => {
  const locale = localStorage.getItem("I18N_LANGUAGE") || "pt-BR";
  request.headers["Accept-Language"] = locale;

  // Token certo por destino da chamada — evita JWT tenant em /super-admin e vice-versa.
  const url = request.url || "";
  const absolute = url.startsWith("http") ? url : `${request.baseURL || ""}${url}`;
  const useSa = isSuperAdminRequestUrl(absolute) || isSuperAdminPage();
  const token = readStoredToken(
    useSa ? SA_AUTH_STORAGE_KEY : TENANT_AUTH_STORAGE_KEY
  );
  if (token) {
    request.headers["Authorization"] = "Bearer " + token;
  } else if (useSa) {
    delete request.headers["Authorization"];
  }

  return request;
});

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    // Devolve o envelope JSON (success/data/error), não o AxiosResponse.
    // Helpers tipam Promise<ApiSuccess<T>> e leem res.data (= domínio).
    // Proibido res.data.data — DOC 09 § 7.2.
    return response.data ? response.data : response;
  },
  (error: AxiosError<ApiFailure>) => {
    const status = error.response?.status;
    const body = error.response?.data;
    const path = window.location.pathname;
    const requestUrl = error.config?.url || "";
    const absolute =
      requestUrl.startsWith("http")
        ? requestUrl
        : `${error.config?.baseURL || ""}${requestUrl}`;

    if (status === 401) {
      const saContext =
        isSuperAdminPage() || isSuperAdminRequestUrl(absolute);

      if (saContext) {
        // Isolamento SA: nunca mandar para /login do tenant (DOC 09 § 4).
        sessionStorage.removeItem(SA_AUTH_STORAGE_KEY);
        if (!path.startsWith("/super-admin/login")) {
          window.location.href = "/super-admin/login";
        }
      } else {
        sessionStorage.removeItem(TENANT_AUTH_STORAGE_KEY);
        delete axios.defaults.headers.common["Authorization"];
        if (!path.startsWith("/login") && !path.startsWith("/register") && !path.startsWith("/unsubscribe") && !path.startsWith("/accept-invite") && !path.startsWith("/owner-invite")) {
          window.location.href = "/login";
        }
      }
    }

    // LAC-197: soft-lock — 403 de assinatura inativa → tela de pausa (DOC 09).
    if (
      status === 403 &&
      body?.error?.code === "SUBSCRIPTION_INACTIVE" &&
      !isSuperAdminPage() &&
      !path.startsWith("/suspended") &&
      !path.startsWith("/settings/subscription")
    ) {
      window.location.href = "/suspended";
    }

    const message =
      body?.error?.message ||
      error.message ||
      "Request failed";

    return Promise.reject({
      status,
      code: body?.error?.code,
      message,
      details: body?.error?.details,
      raw: body,
    });
  }
);

const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

const clearAuthorization = () => {
  delete axios.defaults.headers.common["Authorization"];
};

class APIClient {
  get = (url: string, params?: any): Promise<any> => {
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + "=" + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  create = (url: string, data: any): Promise<any> => {
    return axios.post(url, data);
  };

  update = (url: string, data: any): Promise<any> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<any> => {
    return axios.put(url, data);
  };

  delete = (url: string, config?: AxiosRequestConfig): Promise<any> => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => {
  const user = sessionStorage.getItem(TENANT_AUTH_STORAGE_KEY);
  if (!user) {
    return null;
  }
  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
};

/** Sessão SA — storage separado do tenant. */
const getLoggedinSuperAdmin = () => {
  const raw = sessionStorage.getItem(SA_AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Persiste JWT tenant (login e register — mesmo envelope DOC 06 § 5.1). */
const persistTenantSession = (data: unknown) => {
  sessionStorage.setItem(TENANT_AUTH_STORAGE_KEY, JSON.stringify(data));
  const token =
    data && typeof data === "object"
      ? (data as { access_token?: string; token?: string }).access_token ||
        (data as { token?: string }).token
      : null;
  if (token) {
    setAuthorization(token);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(TENANT_SESSION_UPDATED_EVENT));
  }
};

/**
 * PATCH/GET /me devolve user+tenant sem tokens — mescla na sessão existente.
 * Por quê: sobrescrever authUser com só /me apagaria o JWT.
 */
const mergeTenantMeIntoSession = (me: {
  user: unknown;
  tenant: unknown;
}): void => {
  const current = getLoggedinUser();
  if (!current || typeof current !== "object") {
    return;
  }
  persistTenantSession({
    ...current,
    user: me.user,
    tenant: me.tenant,
  });
};

const persistSuperAdminSession = (data: unknown) => {
  sessionStorage.setItem(SA_AUTH_STORAGE_KEY, JSON.stringify(data));
  const token =
    data && typeof data === "object"
      ? (data as { access_token?: string; token?: string }).access_token ||
        (data as { token?: string }).token
      : null;
  if (token) {
    setAuthorization(token);
  }
};

const clearSuperAdminSession = () => {
  sessionStorage.removeItem(SA_AUTH_STORAGE_KEY);
  if (isSuperAdminPage()) {
    clearAuthorization();
  }
};

export {
  APIClient,
  setAuthorization,
  clearAuthorization,
  getLoggedinUser,
  getLoggedinSuperAdmin,
  persistTenantSession,
  mergeTenantMeIntoSession,
  persistSuperAdminSession,
  clearSuperAdminSession,
};
